import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useOutletContext } from 'react-router-dom';
import { Box, Typography, Stack, Chip, Avatar, Button, CircularProgress, Alert, TextField, MenuItem, Snackbar, IconButton, Tooltip } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { initialsFromName as initials, avatarTone, inr } from '../utils/format';
import { ghostBtn, solidBtn, field } from '../components/uiKit';
import http, { apiErrorMessage } from '../Api/http';
import { GetCandidates, UpdateCandidate } from '../Api/Api';
import { normalizeCandidate, STAGES, SOURCES, titleCase } from '../components/RecruitmentComps/CandidatesTab';

const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '12px', p: 2.5 };

const Section = ({ title, children }) => (
    <Box sx={card}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#7C5CFC', letterSpacing: 0.6, mb: 1.5 }}>{title}</Typography>
        {children}
    </Box>
);

const Field = ({ label, value }) => (
    <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#98A0AE', textTransform: 'uppercase' }}>{label}</Typography>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#0F172A', wordBreak: 'break-word' }}>{value || '—'}</Typography>
    </Box>
);

const Input = ({ label, value, onChange, ...rest }) => (
    <TextField label={label} size="small" fullWidth value={value} onChange={onChange}
        slotProps={{ inputLabel: { shrink: true } }} sx={field} {...rest} />
);

const grid = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2 };

export default function CandidateDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [snack, setSnack] = useState(null);
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data: body } = await http.get(GetCandidates);
                if (body?.error) throw new Error(body.message || 'Could not load candidate.');
                const list = Array.isArray(body?.data?.candidates) ? body.data.candidates : [];
                const found = list.find((x) => String(x.id) === String(id));
                if (!alive) return;
                if (!found) setError('Candidate not found.');
                else setC(normalizeCandidate(found));
            } catch (err) {
                if (alive) setError(err?.response ? apiErrorMessage(err, 'Could not load candidate.') : (err?.message || 'Could not load candidate.'));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [id, reload]);

    const { setCrumb } = useOutletContext() || {};
    useEffect(() => { if (setCrumb && c?.name) setCrumb(c.name); }, [setCrumb, c?.name]);

    const startEdit = () => {
        setForm({
            name: c.name, phone: c.phone, email: c.email, currentCompany: c.currentCompany,
            experience: c.experienceYears ? String(c.experienceYears) : '', expectedSalary: c.expectedSalary ? String(c.expectedSalary) : '',
            noticePeriod: c.noticePeriod, source: c.source || 'Other', city: c.city || '',
        });
        setEditing(true);
    };
    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const save = async () => {
        if (!form.name.trim()) { setSnack({ msg: 'The candidate needs a name.', sev: 'warning' }); return; }
        setSaving(true);
        try {
            const { data: body } = await http.put(UpdateCandidate, {
                id: c.id,
                vacancyId: c.vacancyId,
                fullName: form.name.trim(),
                // A blank email is one shared address on the server; use a unique placeholder (see the add form).
                email: form.email.trim() || `${String(form.phone || '').replace(/\D/g, '') || 'cand'}-${c.id}@noemail.local`,
                phone: form.phone || '',
                currentCompany: form.currentCompany || '',
                experience: Number(form.experience) || 0,
                expectedSalary: Number(form.expectedSalary) || 0,
                noticePeriod: form.noticePeriod || '',
                source: form.source,
                city: form.city.trim(),
            });
            if (body?.error) throw new Error(body.message || 'Could not save the candidate.');
            setSnack({ msg: body?.message || 'Candidate updated.', sev: 'success' });
            setEditing(false);
            setReload((n) => n + 1);
        } catch (err) {
            setSnack({ msg: err?.response ? apiErrorMessage(err, 'Could not save the candidate.') : (err?.message || 'Could not save the candidate.'), sev: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const back = () => navigate('/dashboard/recruitment?tab=candidates');
    const stage = c ? (STAGES[c.stage] || STAGES.applied) : null;
    const exp = c ? (c.experienceYears > 0 ? `${c.experienceYears} yr${c.experienceYears === 1 ? '' : 's'}` : (titleCase(c.leadExperience) || 'Fresher')) : '';

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress size={28} /></Box>}
            {!loading && error && <Alert severity="error">{error}</Alert>}

            {c && (
                <Stack spacing={2}>
                    <Box sx={{ ...card, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Tooltip arrow title="Back to candidates">
                            <IconButton onClick={back} aria-label="Back to candidates" sx={{ border: '1px solid #E6EAF1', borderRadius: '8px', color: '#475569', '&:hover': { bgcolor: '#F1EEFE', color: '#7C5CFC' } }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                        </Tooltip>
                        <Avatar sx={{ width: 60, height: 60, ...avatarTone(c.name), fontSize: 20, fontWeight: 700 }}>{initials(c.name)}</Avatar>
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                            {editing
                                ? <Input label="Full name" value={form.name} onChange={set('name')} />
                                : <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#0F172A' }}>{c.name}</Typography>}
                            <Typography sx={{ fontSize: 13, color: '#64748B' }}>{c.vacancyTitle || 'Unknown role'}{c.entityName ? ` · ${c.entityName}` : ''}</Typography>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#7C5CFC' }}>{c.trackingCode}</Typography>
                        </Box>
                        <Chip label={stage.label} sx={{ fontWeight: 700, color: stage.color, bgcolor: stage.bg }} />
                        {editing ? (
                            <>
                                <Button onClick={() => setEditing(false)} disabled={saving} sx={{ ...ghostBtn, height: 36, px: 1.6 }}>Cancel</Button>
                                <Button onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 36, px: 2 }}>Save changes</Button>
                            </>
                        ) : (
                            <Button startIcon={<EditRoundedIcon sx={{ fontSize: 17 }} />} onClick={startEdit} sx={{ ...ghostBtn, height: 36, px: 1.6 }}>Edit</Button>
                        )}
                    </Box>

                    <Section title="CONTACT & APPLICATION">
                        <Box sx={grid}>
                            {editing ? <Input label="Phone" value={form.phone} onChange={set('phone')} /> : <Field label="Phone" value={c.phone} />}
                            {editing ? <Input label="City" value={form.city} onChange={set('city')} /> : <Field label="City" value={titleCase(c.city)} />}
                            <Field label="Preferred location" value={titleCase(c.locationInterest)} />
                            {editing ? <Input label="Email" value={form.email} onChange={set('email')} /> : <Field label="Email" value={c.email} />}
                            <Field label="Applied on" value={c.appliedOn} />
                            {editing ? (
                                <>
                                    <Input label="Source" select value={SOURCES.includes(form.source) ? form.source : 'Other'} onChange={set('source')}>
                                        {SOURCES.map((x) => <MenuItem key={x} value={x} sx={{ fontSize: 13 }}>{x}</MenuItem>)}
                                    </Input>
                                    <Input label="Experience (years)" type="number" value={form.experience} onChange={set('experience')} />
                                    <Input label="Current company" value={form.currentCompany} onChange={set('currentCompany')} />
                                    <Input label="Expected salary" type="number" value={form.expectedSalary} onChange={set('expectedSalary')} />
                                    <Input label="Notice period" value={form.noticePeriod} onChange={set('noticePeriod')} />
                                </>
                            ) : (
                                <>
                                    <Field label="Source" value={c.source} />
                                    <Field label="Experience" value={exp} />
                                    <Field label="Current company" value={c.currentCompany} />
                                    <Field label="Expected salary" value={c.expectedSalary ? inr(c.expectedSalary) : ''} />
                                    <Field label="Notice period" value={c.noticePeriod} />
                                </>
                            )}
                            <Field label="Interview rounds" value={c.roundsTotal ? `${c.roundsDecided} of ${c.roundsTotal} decided${c.stageRound ? ` · ${c.stageRound}` : ''}` : 'Not scheduled'} />
                            {c.joinedOn && <Field label="Joined on" value={c.joinedOn} />}
                            {c.rejectReason && <Field label="Reject reason" value={c.rejectReason} />}
                        </Box>
                        {c.resumeUrl && (
                            <Button href={c.resumeUrl} target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 16 }} />}
                                sx={{ ...ghostBtn, height: 34, mt: 2, fontSize: 12.5 }}>{c.resumeFileName || 'View resume'}</Button>
                        )}
                    </Section>

                    {c.leadAnswers.length > 0 && (
                        <Section title="LEAD FORM ANSWERS">
                            <Box sx={grid}>
                                {c.leadAnswers.map((a, i) => <Field key={i} label={a.question} value={/^[+\d\s]+$/.test(a.answer || '') ? a.answer : titleCase(a.answer)} />)}
                            </Box>
                            {(c.metaCampaignName || c.metaAdName) && (
                                <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                                    {c.metaCampaignName && <Chip size="small" label={`Campaign: ${c.metaCampaignName}`} />}
                                    {c.metaAdName && <Chip size="small" label={`Ad: ${c.metaAdName}`} />}
                                    {c.metaPlatform && <Chip size="small" label={c.metaPlatform === 'ig' ? 'Instagram' : c.metaPlatform === 'fb' ? 'Facebook' : c.metaPlatform} />}
                                </Stack>
                            )}
                        </Section>
                    )}

                    <Section title={`EMPLOYMENT HISTORY${c.totalEmploymentDuration ? ` · ${c.totalEmploymentDuration}` : ''}`}>
                        {c.employments.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: '#98A0AE' }}>No past roles recorded.</Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {c.employments.map((e, i) => (
                                    <Box key={e.id ?? i}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{e.designation} · {e.company}</Typography>
                                        <Typography sx={{ fontSize: 12, color: '#64748B' }}>{e.fromMonth || '—'} → {e.current ? 'Present' : (e.toMonth || '—')}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Section>

                    <Section title="COMMUNICATION LOG">
                        {c.communications.length === 0 ? (
                            <Typography sx={{ fontSize: 13, color: '#98A0AE' }}>No communication logged yet.</Typography>
                        ) : (
                            <Stack spacing={1.5}>
                                {c.communications.map((m, i) => (
                                    <Box key={m.id ?? i}>
                                        <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{m.communicationType} · {m.status}</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: '#334155' }}>{m.description}</Typography>
                                        <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{m.communicatedOn}{m.loggedBy ? ` · by ${m.loggedBy}` : ''}</Typography>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Section>
                </Stack>
            )}
            <Snackbar open={Boolean(snack)} autoHideDuration={4000} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                {snack ? <Alert severity={snack.sev} onClose={() => setSnack(null)} sx={{ width: '100%' }}>{snack.msg}</Alert> : undefined}
            </Snackbar>
        </Box>
    );
}
